<script>
import { sortBy, keyBy, maxBy } from 'lodash-es';
import { mapActions, mapState } from 'vuex';
import TwOverlay from '../../components/TwOverlay';
import TriggerRulesTemplate from '../../components/Triggers/TriggerRulesTemplate';
import TriggerRulesRevisions from '../../components/Triggers/TriggerRulesRevisions';
import TriggerRulesSettings from '../../components/Triggers/TriggerRulesSettings';
import triggers from '../../variables/triggers';

export default {
  name: 'TriggerRules',
  components: {
    TwOverlay,
    TriggerRulesTemplate,
    TriggerRulesRevisions,
    TriggerRulesSettings,
  },
  data() {
    return {
      panel: null,
      domainId: null,
    };
  },
  computed: {
    ...mapState({
      domains: state => state.triggers.domains,
      triggerTypes: state => state.triggers.types,
      triggerType: state => state.triggers.rules.triggerType,
      domain: state => state.triggers.rules.domain,
      rules: state => state.triggers.rules.rules,
      templates: state => state.triggers.rules.templates,
      update: state => state.triggers.rules.save,
      currentRevision: state => state.triggers.rules.currentRevision,
      revisions: state => state.triggers.rules.revisions,
    }),
    disableDomains() {
      return this.domains.loading ||
        this.domains.error ||
        this.domains.noContent ||
        this.rules.loading ||
        this.update.loading;
    },
    sortedTemplates() {
      return sortBy(this.rules.item.templates, 'order');
    },
    getKeyedTemplates() {
      return keyBy(this.templates.items, 'id');
    },
    validRules() {
      if (!this.rules.item.templates) {
        return false;
      }
      return !this.rules.item.templates.some(t => !t.valid);
    },
    disabled() {
      return !this.rules.item.isLatest;
    },
    defaultExceptions() {
      switch (this.triggerType.id) {
        case 1:
          return [
            {
              id: this.$moment().valueOf(),
              type: 'remark',
              data: { valid: true },
            },
          ];
        default:
          return [];
      }
    },
  },
  methods: {
    ...mapActions({
      setBreadcrumbs: 'nav/setBreadcrumbs',
      getDomains: 'triggers/loadDomains',
      getTriggerTypes: 'triggers/getTriggerTypes',
      setTriggerType: 'triggers/setRuleTriggerType',
      setDomain: 'triggers/setRuleDomain',
      getRevisions: 'triggers/getRevisions',
      addNewTemplate: 'triggers/addTemplate',
      deleteOldTemplate: 'triggers/deleteTemplate',
      swapTemplate: 'triggers/swapTemplate',
      createRevision: 'triggers/createRevision',
      setRevision: 'triggers/setRevision',
      resetRevision: 'triggers/resetRevision',
      getSettings: 'triggers/getSettings',
    }),
    domainChanged(domainId) {
      const foundDomain = this.domains.items.find(domain => domain.id === domainId);
      this.resetRevision();
      this.setDomain(foundDomain);
      this.$router.replace({
        ...this.$router.currentRoute,
        query: {
          domain: foundDomain.id,
        },
      });
    },
    addTemplate() {
      const rule = {
        id: this.$moment().valueOf(),
        templateId: null,
        daysAfterLast: 2,
        timeOfDay: 17,
        exceptions: this.defaultExceptions,
        triggerType: this.triggerType,
        valid: false,
      };
      this.addNewTemplate(rule);
      if (this.sortedTemplates) {
        this.panel = this.sortedTemplates.length - 1;
      } else {
        this.panel = 0;
      }
    },
    deleteTemplate(templateId) {
      this.panel = undefined;
      this.deleteOldTemplate(templateId);
    },
    swapOrder(index, dir) {
      this.swapTemplate({
        from: this.sortedTemplates[index].id,
        to: this.sortedTemplates[index + dir].id,
      });
    },
    getTemplateName(templateId) {
      if (this.getKeyedTemplates[templateId]) {
        return this.getKeyedTemplates[templateId].name;
      }
      return '...';
    },
    saveRevision() {
      if (!this.validRules) {
        return;
      }
      this.createRevision();
    },
    goToLatestRevision() {
      const latest = maxBy(this.revisions.items, 'revision');
      this.setRevision(latest.revision);
    },
    refreshPage() {
      if (this.triggerType) {
        this.getRevisions();
        this.getSettings();
      }
    },
    daysDescription(template) {
      if (this.triggerType.dueDateOptions.length > 1) {
        return template.order === 1 ? 'Days before due' : 'Days after last';
      }
      return this.triggerType.dueDateOptions[0] === triggers.TRIGGER_TYPE_DUE_DATE_BEFORE
        ? 'Days before'
        : 'Days after last';
    },
  },
  created() {
    this.getTriggerTypes();
    this.setBreadcrumbs({
      breadcrumbs: [
        { text: 'Triggers', to: '', disabled: true },
        { text: 'Rules', to: this.$route.path },
      ],
      title: 'Rules',
    });
    if (!this.domains.items.length && !this.domains.loading) {
      this.getDomains().then(() => {
        if (this.$route.query.revision) {
          const revision = parseInt(this.$route.query.revision, 10);
          this.setRevision(revision);
        }
        if (this.$route.query.domain) {
          const domainId = parseInt(this.$route.query.domain, 10);
          this.domainId = domainId;
          this.setDomain(this.domains.items.find(domain => domain.id === domainId));
        }
      });
    }
  },
};
</script>
<template>
  <v-flex xs12>
    <v-card>
      <v-container grid-list>
        <v-layout row mb-3>
          <v-flex xs6>
            <v-autocomplete
              :items="domains.items"
              :loading="domains.loading"
              :disabled="disableDomains"
              :value="domainId"
              label="Choose Domain"
              item-value="id"
              item-text="name"
              dense
              @change="domainChanged"
            />
          </v-flex>
          <v-flex xs6>
            <v-autocomplete
              :value="triggerType"
              :items="triggerTypes.items"
              :loading="triggerTypes.loading"
              :disabled="triggerTypes.loading || triggerTypes.error"
              label="Choose Trigger Type"
              item-value="id"
              item-text="type"
              return-object
              @change="setTriggerType"
            />
          </v-flex>
          <v-spacer/>
          <v-flex xs1 text-xs-right>
            <v-btn
              :disabled="rules.loading || domains.loading || !domain"
              :class="{ 'icon-spin': rules.loading }"
              icon
              @click="refreshPage"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>

        <v-divider v-if="!domain"/>

        <div v-if="domain && triggerType" class="trigger-rules__content">
          <tw-overlay :visible="rules.loading || update.loading"/>
          <v-progress-circular
            v-if="rules.loading || update.loading"
            class="trigger-rules__content-loading"
            indeterminate
          />

          <trigger-rules-settings
            :trigger-type="triggerType"
            @refresh="refreshPage"
          />

          <v-layout row mt-5>
            <v-flex xs12 headline>
              Trigger <b>{{ triggerType.type }}</b> Rules for <b>{{ domain.name }}</b>
              <template v-if="!rules.loading">
                -
                Revision: <b>{{ currentRevision }}</b>
              </template>
            </v-flex>
          </v-layout>

          <v-divider/>

          <v-layout v-if="rules.error">
            <v-flex xs12 mt-4 text-xs-center>
              <span class="caption grey--text">Unable to load trigger rules</span>
            </v-flex>
          </v-layout>

          <v-layout v-else row mt-4 wrap>
            <v-flex xs12>
              <v-btn :disabled="disabled" color="info" block @click="addTemplate">
                Add New Trigger
              </v-btn>
              <v-expansion-panel v-model="panel" v-if="rules.item.templates">
                <v-expansion-panel-content
                  v-for="(template, index) in sortedTemplates"
                  :key="`revision-${currentRevision || 1}-template-${template.id}`"
                  hide-actions
                >
                  <template v-slot:header>
                    <v-flex grow>
                      <b>{{ triggerType.type }} {{ template.order }}</b>:
                      <div v-if="template.valid" class="d-inline">
                        <v-icon color="green" class="px-2" small>check</v-icon>
                        <span class="caption grey--text pl-2">
                          Template: {{ getTemplateName(template.templateId) }}
                        </span>
                        <span class="caption grey--text pl-2">
                          {{ daysDescription(template) }}:
                          {{ template.daysAfterLast }}
                        </span>
                        <span class="caption grey--text pl-2">
                          Time of day:
                          <template v-if="template.timeOfDay < 10">
                            0{{ template.timeOfDay }}:00
                          </template>
                          <template v-else>
                            {{ template.timeOfDay }}:00
                          </template>
                        </span>
                        <span class="caption grey--text pl-2">
                          Exceptions: {{ template.exceptions.length }}
                        </span>
                      </div>
                      <span v-else class="caption error--text">Missing setup</span>
                    </v-flex>
                    <v-flex shrink>
                      <v-btn
                        :disabled="template.order === sortedTemplates.length || disabled"
                        class="ma-0"
                        icon
                        small
                        @click.stop="swapOrder(index, 1)"
                      >
                        <v-icon small>south</v-icon>
                      </v-btn>
                      <v-btn
                        :disabled="template.order === 1 || disabled"
                        class="ma-0"
                        icon
                        small
                        @click.stop="swapOrder(index, -1)"
                      >
                        <v-icon small>north</v-icon>
                      </v-btn>
                      <v-btn
                        :disabled="disabled"
                        class="ma-0"
                        icon
                        small
                        @click.stop="deleteTemplate(template.id)"
                      >
                        <v-icon small>delete</v-icon>
                      </v-btn>
                    </v-flex>
                  </template>
                  <v-divider/>
                  <v-card-text>
                    <trigger-rules-template :template="template" :disabled="disabled"/>
                  </v-card-text>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-flex>
            <v-flex xs12 text-xs-center mt-4>
              <span v-if="!sortedTemplates.length" class="caption grey--text">
                Click button above to add a reminder
              </span>
            </v-flex>
          </v-layout>

          <v-layout justify-end mt-2>
            <v-flex shrink>
              <span v-if="!rules.item.isLatest" class="caption grey--text">
                Can't edit an old revision
                <a class="external-link cursor-pointer" @click="goToLatestRevision">Go to Latest</a>
              </span>
              <v-btn
                :disabled="!validRules || !rules.item.isLatest"
                class="justify-end mr-0"
                color="info"
                @click="saveRevision"
              >
                Save
              </v-btn>
            </v-flex>
          </v-layout>

          <trigger-rules-revisions
            :current-revision="currentRevision"
          />
        </div>

        <v-layout v-else mt-3>
          <v-flex xs12 text-xs-center>
            <span class="caption grey--text">
              Select domain and trigger type to see corresponding trigger rules
            </span>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card>
  </v-flex>
</template>

<style lang="scss">
.trigger-rules {
  &__content {
    position: relative;

    &-loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 100;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/triggers/TriggerRules.vue